import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Globe, Plus, Trash2, ShieldCheck, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from './AdminLayout';

const QUERY_KEY = '/api/admin/trusted-domains';

interface TrustedDomain {
  id: number;
  domain: string;
  active: boolean;
  createdAt: string;
}

interface TrustedDomainsResponse {
  domains: TrustedDomain[];
}

export default function AdminTrustedDomains() {
  const { toast } = useToast();
  const [newDomain, setNewDomain] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<TrustedDomainsResponse>({
    queryKey: [QUERY_KEY],
  });

  const domainList: TrustedDomain[] = data?.domains ?? [];

  const addMutation = useMutation({
    mutationFn: (domain: string) =>
      apiRequest('POST', QUERY_KEY, { domain }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      setNewDomain('');
      toast({ title: 'Domain added', description: `${newDomain} is now a trusted domain.` });
    },
    onError: (err: any) => {
      toast({
        title: 'Failed to add domain',
        description: err?.message || 'An error occurred.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (domain: string) =>
      apiRequest('DELETE', `${QUERY_KEY}/${encodeURIComponent(domain)}`),
    onSuccess: (_data, domain) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      setConfirmDelete(null);
      toast({ title: 'Domain removed', description: `${domain} has been removed.` });
    },
    onError: (err: any) => {
      toast({
        title: 'Failed to remove domain',
        description: err?.message || 'An error occurred.',
        variant: 'destructive',
      });
    },
  });

  const handleAdd = () => {
    const trimmed = newDomain.trim();
    if (!trimmed) return;
    addMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleAdd();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Trusted Domains</h1>
          <p className="text-muted-foreground">
            Manage domains trusted by the Eatzy Auth Hub for OAuth callbacks and CORS.
          </p>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Auth Hub Trusted Domains
              </CardTitle>
              <CardDescription>
                Changes take effect within 5 minutes or immediately upon add/remove.
                This operation requires super-admin access.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="e.g. newrestaurant.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={handleKeyDown}
                data-testid="input-new-domain"
              />
              <Button
                onClick={handleAdd}
                disabled={!newDomain.trim() || addMutation.isPending}
                data-testid="button-add-domain"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : error ? (
              <div className="flex items-center gap-2 text-destructive py-4">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">
                  {(error as any)?.message || 'Failed to load trusted domains. You may need super-admin access.'}
                </span>
              </div>
            ) : domainList.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
                <Globe className="w-8 h-8" />
                <p className="text-sm">No trusted domains configured yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {domainList.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md bg-muted/50"
                    data-testid={`domain-row-${d.domain}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{d.domain}</span>
                      {d.active ? (
                        <Badge variant="secondary" className="text-xs hidden sm:inline-flex">trusted</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs hidden sm:inline-flex text-muted-foreground">inactive</Badge>
                      )}
                    </div>

                    {confirmDelete === d.domain ? (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">Remove?</span>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteMutation.mutate(d.domain)}
                          disabled={deleteMutation.isPending}
                          data-testid={`button-confirm-delete-${d.domain}`}
                        >
                          Yes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmDelete(null)}
                          data-testid={`button-cancel-delete-${d.domain}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirmDelete(d.domain)}
                        data-testid={`button-delete-${d.domain}`}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
